import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Dialog, Grid } from '@mui/material';
import { GroupByReturnType } from 'utils/array';
import MediaModal from './MediaModal';
import MediaGalleryThumbnail from './MediaGalleryThumbnail';

const StyledDialog = styled(Dialog)`
  .MuiPaper-root {
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    margin: 0;
    border-radius: 0;
    background: transparent;
  }
`;

interface MediaGalleryInterface {
  files?: MediaGalleryFileInterface[];
  groupedFiles?: GroupByReturnType<MediaGalleryFileInterface>;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  onClickDelete?: (file: MediaGalleryFileInterface) => void;
  groupByKey?: keyof MediaGalleryFileInterface;
  hideComments?: boolean;
}

export interface MediaGalleryFileInterface {
  url: string;
  caption?: string;
  id?: number;
  index?: number;
  link?: string;
  fileType: string;
  store?: string;
  mediaComments?: {
    id: number;
    body?: string;
    user?: {
      id: number;
      firstName: string;
      lastName: string;
    };
    createdAt?: Date;
  }[];
}

const MediaGallery = ({
  files,
  groupedFiles,
  xs,
  sm,
  md,
  lg,
  xl,
  onClickDelete,
  hideComments,
}: MediaGalleryInterface) => {
  // storing index in case we want to make it so we can increment or decrement through files while modal is open
  const [selectedFile, setSelectedFile] = useState<{
    file: MediaGalleryFileInterface;
    index: number;
  }>();

  useEffect(() => {
    if (selectedFile) {
      const updatedFile = files?.filter((f) => f.id === selectedFile.file.id)[0];

      if (updatedFile) {
        setSelectedFile({
          file: updatedFile,
          index: selectedFile.index,
        });
      }
    }
  }, [files]);

  const getMediaCount = () => {
    if (files) {
      return files.length;
    } else if (groupedFiles) {
      let count = 0;
      Object.values(groupedFiles).forEach((value) => value.forEach((val) => count++));
      return count;
    } else return null;
  };

  const handleCloseDialog = () => {
    setSelectedFile(undefined);
  };

  const mediaCount = useMemo(() => getMediaCount(), [files, groupedFiles]);

  return (
    <>
      {!!mediaCount && <small>{mediaCount} file(s)</small>}
      <Grid container spacing={1}>
        {groupedFiles ? (
          <>
            {Object.entries(groupedFiles).map((group) => {
              if (group[0] === '') return null;

              return (
                <Grid item xs={12} key={group[0]}>
                  <h2>{group[0]}</h2>
                  <Grid container>
                    {group[1].map((file, index) => (
                      <MediaGalleryThumbnail
                        key={file.id}
                        file={file}
                        index={index}
                        onClick={setSelectedFile}
                        onClickDelete={onClickDelete}
                        xs={xs}
                        sm={sm}
                        md={md}
                        lg={lg}
                        xl={xl}
                        hideComments={hideComments}
                      />
                    ))}
                  </Grid>
                </Grid>
              );
            })}

            <h2>Other</h2>
            <Grid container>
              {groupedFiles[''].map((file, index) => (
                <MediaGalleryThumbnail
                  key={file.id}
                  file={file}
                  index={index}
                  onClick={setSelectedFile}
                  onClickDelete={onClickDelete}
                  xs={xs}
                  sm={sm}
                  md={md}
                  lg={lg}
                  xl={xl}
                  hideComments={hideComments}
                />
              ))}
            </Grid>
          </>
        ) : (
          <>
            {files?.map((file, index) => (
              <MediaGalleryThumbnail
                key={index}
                file={file}
                index={index}
                onClick={hideComments ? () => {} : setSelectedFile}
                onClickDelete={onClickDelete}
                xs={xs}
                sm={sm}
                md={md}
                lg={lg}
                xl={xl}
                hideComments={hideComments}
              />
            ))}
          </>
        )}
      </Grid>
      <StyledDialog maxWidth='xl' open={!!selectedFile} onClose={() => setSelectedFile(undefined)}>
        <MediaModal
          url={selectedFile?.file.url}
          caption={selectedFile?.file.caption}
          comments={selectedFile?.file.mediaComments}
          id={selectedFile?.file.id}
          fileType={selectedFile?.file.fileType}
          onClose={handleCloseDialog}
        />
      </StyledDialog>
    </>
  );
};

export default MediaGallery;
